import toast from 'react-hot-toast';
import { getCancelTournamentEndpoint } from 'constants/payloads/tournaments/cancel';
import { useApiGateway } from 'hooks/useApi';
import { useGameInvites } from 'hooks/useGameInvites';
import { useModal } from 'hooks/useModal';
import Pencil from 'svg/Pencil';
import { withInvitedPlayers } from 'screens/Find/withInvitedPlayers';
import { Button } from 'components/Button';
import { InvitePlayersModal } from 'components/Connections';
import DetailModalShell from 'components/Find/DetailModalShell';
import {
  MatchDescription,
  MatchLocationDetails,
  MatchOrganizer,
  MatchPrice,
  MatchSummary,
} from 'components/Find/MatchDetails';
import { GameFormat, isUnlimitedOpenPlay } from 'components/Find/gameFormat';
import AlertModal from 'components/modals/AlertModal';
import HostRequestsCard from './HostRequestsCard';
import type { HostPlayRequest } from './types';
import { useHostGameDetail } from './useHostGameDetail';

interface Props {
  eventId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Opens the request review modal for the tapped requester (wired by the parent). */
  onSeeRequest: (request: HostPlayRequest) => void;
  /** Opens the game edit flow. Absent for this slice — Edit is then disabled. */
  onEdit?: () => void;
  /** Fired once the game is cancelled, so the caller can refetch and navigate. */
  onCancelled?: () => void;
}

/**
 * The host's view of one of their own pickup games: the same summary + detail
 * sections as the public viewer modal, plus an embedded card of pending join
 * requests (each opens the review modal via `onSeeRequest`), seat-level invite
 * actions, and a Cancel game / Edit footer instead of "Request to play".
 */
export default function HostGameDetailModal({
  eventId,
  isOpen,
  onClose,
  onSeeRequest,
  onEdit,
  onCancelled,
}: Props) {
  const { game, requests } = useHostGameDetail(eventId);
  const cancelModal = useModal();
  const inviteModal = useModal();
  const { post: cancelEvent, isLoading: isCancelling } = useApiGateway<undefined, unknown>();
  const {
    invitedPlayerIds,
    pendingPlayerIds,
    pendingInvitedPlayers,
    isSubmitting: isSavingInvites,
    saveInvites,
  } = useGameInvites(eventId);

  // Anyone invited who is no longer pending has accepted, so their seat is real.
  const acceptedPlayerIds = invitedPlayerIds.filter((id) => !pendingPlayerIds.includes(id));
  // Pending invitees join the drawn roster so the host sees who they're waiting on.
  const { game: gameWithInvites, overflow: overflowInvited } = game
    ? withInvitedPlayers(game, pendingInvitedPlayers)
    : { game: null, overflow: [] };

  const handleCancelGame = async () => {
    if (!eventId) return;
    const response = await cancelEvent({ endpoint: getCancelTournamentEndpoint(eventId) });
    if (response.isError) {
      toast.error(response.error || 'Could not cancel the game');
      return;
    }
    cancelModal.closeModal();
    onClose();
    onCancelled?.();
  };

  const isOpenPlay = game?.format === GameFormat.OpenPlay;

  const footer = (
    <div className="flex gap-ds-md lg:gap-ds-xl">
      <Button variant="brand-secondary" size="lg" onClick={() => cancelModal.openModal()}>
        Cancel game
      </Button>
      <Button
        variant="secondary"
        size="lg"
        iconLeft={<Pencil className="h-5 w-5" />}
        onClick={onEdit}
        disabled={!onEdit}
      >
        Edit
      </Button>
    </div>
  );

  return (
    <>
      {/* One sheet at a time: the detail shell steps aside while the picker is up,
          and comes straight back when it closes — the parent's state never changes. */}
      <DetailModalShell
        isOpen={isOpen && !inviteModal.isOpen}
        onClose={onClose}
        footer={footer}
        sizeDesktop="lg"
        hasFooterDividerAtDesktop
      >
        {gameWithInvites && (
          <>
            <MatchSummary
              game={gameWithInvites}
              sizeDesktop="lg"
              onInviteOpenSeat={isOpenPlay ? undefined : () => inviteModal.openModal()}
              onInvitePlayers={isOpenPlay ? () => inviteModal.openModal() : undefined}
            />

            {overflowInvited.length > 0 && (
              <p className="typography-product-text-card-mobile mt-ds-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Also invited: {overflowInvited.map((player) => player.name).join(', ')}. They can
                still accept if a seat opens up.
              </p>
            )}

            {requests.length > 0 && (
              <div className="mt-ds-xl">
                <HostRequestsCard requests={requests} onSeeRequest={onSeeRequest} />
              </div>
            )}

            {gameWithInvites.details && (
              <>
                <div className="my-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

                <div className="flex flex-col gap-ds-3xl">
                  <MatchOrganizer organizer={gameWithInvites.details.organizer} />
                  <MatchDescription
                    audience={gameWithInvites.details.audience}
                    audienceLabel={gameWithInvites.details.audienceLabel}
                    description={gameWithInvites.details.description}
                    participantLimit={gameWithInvites.participantLimit}
                    isUnlimited={isUnlimitedOpenPlay(gameWithInvites)}
                  />
                  <MatchLocationDetails
                    venue={gameWithInvites.details.venue}
                    isCourtBooked={gameWithInvites.isCourtBooked}
                  />
                  <MatchPrice
                    courtCostPerPlayer={gameWithInvites.details.courtCostPerPlayer}
                    paymentMethod={gameWithInvites.details.paymentMethod}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DetailModalShell>

      <InvitePlayersModal
        isOpen={inviteModal.isOpen}
        selectedPlayerIds={invitedPlayerIds}
        lockedPlayerIds={acceptedPlayerIds}
        isSubmitting={isSavingInvites}
        onClose={inviteModal.closeModal}
        onSubmit={async (playerIds) => {
          const didSave = await saveInvites(playerIds);
          if (didSave) {
            inviteModal.closeModal();
          }
        }}
      />

      <AlertModal
        isOpen={cancelModal.isOpen}
        handleClose={cancelModal.closeModal}
        title="Cancel game"
        description="Everyone in this game will be notified and it will be removed from Games."
        confirmBtnText="Cancel game"
        confirmBtnVariant="brand"
        cancelBtnText="Go back"
        secondaryFirst
        loading={isCancelling}
        onConfirm={handleCancelGame}
        onCancel={cancelModal.closeModal}
      />
    </>
  );
}
